import {
  Box,
  Button,
  Checkbox,
  Grid,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useFileFolderSelection } from '../../../components/Providers/FileFolderSelectionProvider';
import { KPI_FIELDS } from '../../../constants/SemaConstants';
import { useFetchData } from '../../../hooks/useFetchData';
import { KpiMetric } from '../../../Types/dealGPT';
import { getIconForFile } from '../../../util/fileUtils';
import { projectKpisRunPathUrl } from '../../../util/routesUrls';

interface EditAndConfirmDataStepProps {
  onCancel: () => void;
  onSave: (edited: KpiMetric[], selectedRepos: string[]) => Promise<void>;
  isLoading?: boolean;
  isEditingExistingRun?: boolean;
  projectId: string;
  run: string | null;
}

const getUniqueRepositories = (kpis: KpiMetric[]): string[] => {
  const repoSet = new Set<string>();
  kpis.forEach((kpi) => {
    kpi.detail?.repositories?.forEach((repo) => repoSet.add(repo.name));
  });
  return Array.from(repoSet).sort();
};

const EditAndConfirmDataStep = ({
  onCancel,
  onSave,
  isLoading,
  isEditingExistingRun,
  projectId,
  run,
}: EditAndConfirmDataStepProps): JSX.Element => {
  const { selectedFiles } = useFileFolderSelection();
  const { data: kpis, refetch: refetchKpis } = useFetchData<KpiMetric[]>({
    urlPath: projectKpisRunPathUrl(projectId, run ?? ''),
    skip: !run,
  });

  const [selectedRepos, setSelectedRepos] = useState<string[]>([]);
  const [editedKpis, setEditedKpis] = useState<KpiMetric[]>([]);

  const repositories = useMemo(() => getUniqueRepositories(kpis ?? []), [kpis]);
  const isAllSelected = useMemo(() => {
    return selectedRepos.length === repositories.length;
  }, [selectedRepos, repositories]);

  const getKpiInputValue = useCallback(
    (name: string): number => {
      const kpi = editedKpis.find((k) => k.name === name);

      return (
        kpi?.detail?.repositories?.reduce((sum, repo) => {
          if (!selectedRepos.includes(repo.name)) return sum;
          return sum + parseFloat(repo.value ?? '0');
        }, 0) ?? 0
      );
    },
    [editedKpis, selectedRepos]
  );

  const derivedMetrics = useMemo(() => {
    const loc = getKpiInputValue('linesOfCode');
    const high = getKpiInputValue('highCVEs');
    const medium = getKpiInputValue('mediumCVEs');
    const low = getKpiInputValue('lowCVEs');

    const totalCves = high + medium + low;

    const safeLoc = loc || 1;
    const safeTotalCves = totalCves || 1;

    return ['highCVEDensity', 'totalVulnerabilityDensity', 'highCVEPercentage'].map(
      (name): KpiMetric => {
        const existing = editedKpis.find((m) => m.name === name);

        let value = 0;
        if (name === 'highCVEDensity') value = high / safeLoc;
        else if (name === 'totalVulnerabilityDensity') value = totalCves / safeLoc;
        else if (name === 'highCVEPercentage') value = (high / safeTotalCves) * 100;

        return {
          id: existing?.id ?? `derived-${name}`,
          name,
          value: Number(value.toFixed(6)).toString(),
        };
      }
    ) as KpiMetric[];
  }, [editedKpis, getKpiInputValue]);

  const handleRepoChange = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      const value = event.target.value as string[];

      if (value.includes('selectAll')) {
        if (isAllSelected) {
          setSelectedRepos([]);
        } else {
          setSelectedRepos([...repositories]);
        }
      } else {
        setSelectedRepos(value);
      }
      // We want to reset editedKpis to original kpis when changing repo selection
      if (kpis) setEditedKpis(kpis);
    },
    [kpis, isAllSelected, repositories]
  );

  const handleInputChange = useCallback(
    (name: string, newValue: number) => {
      setEditedKpis((prev) => {
        const updated = prev.map((kpi) => {
          if (kpi.name !== name) return kpi;

          const numRepos = selectedRepos.length;
          const baseValue = Math.floor(newValue / numRepos);
          const remainder = newValue % numRepos;

          const updatedRepos = [
            ...(kpi.detail?.repositories ?? []).filter((r) => !selectedRepos.includes(r.name)),
            ...selectedRepos.map((repoName, index) => ({
              name: repoName,
              value: (baseValue + (index < remainder ? 1 : 0)).toString(),
              selected: true,
            })),
          ];

          return {
            ...kpi,
            detail: { repositories: updatedRepos },
          };
        });

        return updated;
      });
    },
    [selectedRepos]
  );

  const metricInsights = useMemo(() => {
    const high = getKpiInputValue('highCVEs');
    const medium = getKpiInputValue('mediumCVEs');
    const low = getKpiInputValue('lowCVEs');
    const loc = getKpiInputValue('linesOfCode');

    const totalCves = high + medium + low;

    const safeLoc = loc || 1;
    const safeTotalCves = totalCves || 1;

    return [
      {
        title: 'High CVE Density',
        formula: 'High CVEs / Total Lines of Code',
        insight: 'Tracks the occurrence of high CVEs in the codebase.',
        calc: `${high} / ${loc.toLocaleString()}`,
        result: (high / safeLoc).toFixed(6),
      },
      {
        title: 'Total Vulnerability Density',
        formula: '(High + Medium + Low CVEs) / Total Lines of Code',
        insight: 'Provides an overall vulnerability rate in the codebase.',
        calc: `${high} + ${medium} + ${low} / ${loc.toLocaleString()}`,
        result: (totalCves / safeLoc).toFixed(6),
      },
      {
        title: 'High CVE Percentage',
        formula: '(High / Total CVEs) * 100',
        insight: 'Measures the proportion of vulnerabilities that are high.',
        calc: `(${high} / (${high} + ${medium} + ${low})) * 100`,
        result: `${((high / safeTotalCves) * 100).toFixed(2)}%`,
      },
    ];
  }, [getKpiInputValue]);

  const handleSave = useCallback(async () => {
    const filteredKpis = editedKpis.filter(
      (kpi) => !derivedMetrics.some((m) => m.name === kpi.name)
    );

    await onSave([...filteredKpis, ...derivedMetrics], selectedRepos);
    // Refetch the kpis after saving
    refetchKpis();
  }, [onSave, editedKpis, derivedMetrics, selectedRepos, refetchKpis]);

  // Initialize editedKpis and selectedRepos when kpis data is fetched
  useEffect(() => {
    if (!kpis) return;

    setEditedKpis(kpis);

    const selected = new Set<string>();
    kpis.forEach((kpi) => {
      kpi.detail?.repositories?.forEach((repo) => {
        if (repo.selected) selected.add(repo.name);
      });
    });

    const derivedRepos = getUniqueRepositories(kpis);
    setSelectedRepos(selected.size > 0 ? Array.from(selected) : derivedRepos);
  }, [kpis]);

  const getRepoDisplayLabel = (selected: string[], maxShown = 2): string => {
    if (selected.length === 0) return 'Select Repositories';
    if (selected.length > maxShown) {
      return `${selected.slice(0, maxShown).join(', ')} + ${selected.length - maxShown} more`;
    }
    return selected.join(', ');
  };

  return (
    <Box mt={4} mb={6}>
      {selectedFiles.length > 0 && (
        <Box
          display="flex"
          flexWrap="wrap"
          gap={2}
          p={2}
          border="1px solid #ccc"
          borderRadius={2}
          mb={2}
        >
          {selectedFiles.map((file) => (
            <Box
              key={file.id}
              display="flex"
              alignItems="center"
              border="1px solid #e0e0e0"
              borderRadius={1}
              px={2}
              py={1}
              bgcolor="#f9f9f9"
            >
              {getIconForFile(file.fileName)}
              <Typography ml={1} variant="body2">
                {file.fileName}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      <Box mb={4}>
        <Box display="flex" alignItems="center" gap={2} mb={1}>
          <Typography variant="subtitle1" fontWeight="bold">
            Repositories:
          </Typography>
          <Select
            multiple
            displayEmpty
            value={selectedRepos}
            onChange={handleRepoChange}
            input={<OutlinedInput size="small" />}
            renderValue={(selected) => getRepoDisplayLabel(selected)}
            sx={{ minWidth: 300 }}
          >
            <MenuItem value="selectAll">
              <Checkbox
                checked={isAllSelected}
                indeterminate={selectedRepos.length > 0 && !isAllSelected}
              />
              <ListItemText
                primary={isAllSelected ? 'DESELECT ALL REPOSITORIES' : 'SELECT ALL REPOSITORIES'}
              />
            </MenuItem>
            {repositories.map((repo) => (
              <MenuItem key={repo} value={repo}>
                <Checkbox checked={selectedRepos.includes(repo)} />
                <ListItemText primary={repo} />
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>
      <Grid container spacing={4}>
        <Grid item xs={12} md={4}>
          <Grid container spacing={2}>
            {KPI_FIELDS.map((field) => (
              <Grid item xs={12} key={field.name}>
                <TextField
                  label={field.label}
                  type="number"
                  fullWidth
                  size="small"
                  sx={{
                    maxWidth: 300,
                    '& input[type=number]': {
                      MozAppearance: 'textfield',
                    },
                    '& input[type=number]::-webkit-outer-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                    '& input[type=number]::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                  }}
                  inputProps={{
                    min: 0,
                    step: 1,
                  }}
                  value={Number(getKpiInputValue(field.name)) || 0}
                  onChange={(e) =>
                    handleInputChange(field.name, Math.max(0, parseInt(e.target.value) || 0))
                  }
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
        <Grid item xs={12} md={8}>
          <Box border="1px solid #D9D9D9" borderRadius={2} padding={3} bgcolor="white">
            {metricInsights.map((metric) => (
              <Box key={metric.title} mb={4}>
                <Grid container spacing={2}>
                  <Grid item xs={8}>
                    <Typography fontWeight={600}>{metric.title}</Typography>
                    <Typography fontSize="14px" color="text.secondary">
                      • Formula: {metric.formula} <br />• Insight: {metric.insight}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography mt={1}>
                      <strong>Calculation:</strong> {metric.calc}
                    </Typography>
                    <Typography>
                      <strong>Result:</strong> {metric.result}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Box>
        </Grid>
      </Grid>
      <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
        <Button variant="outlined" onClick={onCancel}>
          {isEditingExistingRun ? 'Make a new run' : 'Cancel'}
        </Button>
        <Button disabled={isLoading} variant="contained" onClick={handleSave}>
          Save
        </Button>
      </Box>
    </Box>
  );
};

export default EditAndConfirmDataStep;
